import React from 'react';
import PropTypes from 'prop-types';

import { Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import { useFormikContext } from 'formik';

import { ActionButton } from 'core/components/action-button';
import closeIcon from 'core/assets/svg/x.svg';
import { Label } from 'core/components/label';
import useSession from 'core/hooks/use-session';
import { NotificationCard } from 'core/components/notification-card';
import AgencySupportBannerExtended from 'core/components/agency-support-banner-extended';
import { policyType } from '@ourbranch/lookups';
import { useCurrentState } from 'common/hooks/useCurrentState';
import useStyles from './roof-surface-coverage-modal.styles';

const RoofSurfaceCoverageModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { isAgency } = useSession();
  const { values: formikValues } = useFormikContext();
  const state = useCurrentState(formikValues, { policyType: policyType.Home });

  const underwritingSentence = isAgency ? (
    <>
      You can find a copy of the Roof Surfaces payment schedule in the{' '}
      <a className={classes.link} href="/portal/underwriting" rel="noreferrer noopener" target="_blank">
        Underwriting section
      </a>{' '}
      of your agency portal.
    </>
  ) : (
    'External agents may find a copy of the payment schedule in the Underwriting section of their agency portal.'
  );
  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.container }}>
      <ActionButton type="edit" size="big" icon={closeIcon} className={classes.closeButton} onClick={onClose} />
      <DialogTitle className={classes.title}>
        <Label type="titleSecondary">Roof surfaces coverages</Label>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Label type="infoInnerTitleBold" className={classes.text}>
          We offer {state === 'KY' ? 'three' : 'two'} types of coverage for Roof surfaces:
        </Label>

        <p className={classes.paragraph}>
          <Label type="infoInnerTitleBold">Standard (Payment Schedule)</Label>
          <br />
          If the roof is damaged by wind or hail and the member has an eligible claim, it will be{' '}
          <strong>
            paid using a payment schedule determined by roof age and material and not by replacement cost.
          </strong>{' '}
          The older the roof, the less Branch will pay of the cost of the roof replacement. {underwritingSentence}
        </p>

        <p className={classes.paragraph}>
          <Label type="infoInnerTitleBold">Replacement Cost (Extended)</Label>
          <br />
          If the roof is damaged by wind or hail and the member has an eligible claim, it will be{' '}
          <strong>paid using the replacement cost with no application of a payment schedule.</strong> This means that
          the claim is paid up to the amount of money needed to repair or replace the roof at today's material prices.
        </p>

        {state === 'KY' && (
          <p className={classes.paragraph}>
            <Label type="infoInnerTitleBold">Fortified Roof Upgrade Endorsement</Label>
            <br />
            If the roof is damaged by wind or hail and the member has an eligible claim, it will be replaced to meet
            IBHS standards for a Fortified Home. Note: If the home already has a Fortified Home Certification, this
            coverage selection is unnecessary, and therefore unavailable. This coverage is only available when also
            selecting Replacement Cost (Extended) Coverage
          </p>
        )}

        <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
          <Label className={classes.notificationCardText}>
            We may or may not offer {state === 'KY' ? 'all' : 'both'} of these coverage options depending on the State
            and age of the roof.
          </Label>
        </NotificationCard>
      </DialogContent>
      {isAgency && <AgencySupportBannerExtended content="If you have questions, please contact us." />}
    </Dialog>
  );
};

RoofSurfaceCoverageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default RoofSurfaceCoverageModal;
